import dayjs from "dayjs";
import {
  CustomerServiceMode,
  SpecialOrderCashPaymentType,
  SpecialOrderDocType,
  SpecialOrderMode,
  SpecialOrderPendingPickup,
  SpecialOrderStatus,
  SpecialOrderUndelivered,
  SpecialOrderUndeliveredReason,
} from "../../types/special-order.type";

export const SPECIAL_ORDER_PATH = "/special-orders";
export const SPECIAL_ORDER_NEW_PATH = `${SPECIAL_ORDER_PATH}/new`;
export const specialOrderDetailPath = (uuid: string) =>
  `${SPECIAL_ORDER_PATH}/${uuid}`;
export const specialOrderEditPath = (uuid: string) =>
  `${SPECIAL_ORDER_PATH}/${uuid}/edit`;
/** "ออกใหม่ (คัดลอกรายการ)": a new draft prefilled from a cancelled order. */
export const COPY_FROM_PARAM = "copy_from";
export const specialOrderCopyPath = (uuid: string) =>
  `${SPECIAL_ORDER_NEW_PATH}?${COPY_FROM_PARAM}=${encodeURIComponent(uuid)}`;

type ChipColor = "default" | "primary" | "warning" | "success" | "error" | "info";

export const STATUS_LABEL: Record<SpecialOrderStatus, string> = {
  draft: "ร่าง",
  ordered: "ยืนยันสั่ง",
  delivered: "ยืนยันส่งแล้ว",
  cancelled: "ยกเลิก",
};

export const STATUS_COLOR: Record<SpecialOrderStatus, ChipColor> = {
  draft: "default",
  ordered: "warning",
  delivered: "success",
  cancelled: "error",
};

export const STATUS_FILTER: { value: SpecialOrderStatus | "all"; label: string }[] = [
  { value: "all", label: "ทั้งหมด" },
  { value: "draft", label: STATUS_LABEL.draft },
  { value: "ordered", label: STATUS_LABEL.ordered },
  { value: "delivered", label: STATUS_LABEL.delivered },
  { value: "cancelled", label: STATUS_LABEL.cancelled },
];

export const MODE_LABEL: Record<SpecialOrderMode, string> = {
  A: "แบบ A · สายส่งไปส่ง",
  B: "แบบ B · ลูกค้ามารับ",
};

export const MODE_SHORT_LABEL: Record<SpecialOrderMode, string> = {
  A: "A",
  B: "B",
};

/** Mode A = the van (amber, the fleet's colour), mode B = the branch counter (primary). */
export const MODE_COLOR: Record<SpecialOrderMode, "warning" | "primary"> = {
  A: "warning",
  B: "primary",
};

/** The date means something different per mode: the van delivers it, or the customer comes. */
export const deliveryDateLabel = (mode: SpecialOrderMode) =>
  mode === "B" ? "วันที่ลูกค้ามารับ" : "วันที่ส่งของ";

export const SERVICE_MODE_OPTIONS: { value: CustomerServiceMode | ""; label: string }[] = [
  { value: "", label: "ไม่ระบุ" },
  { value: "A", label: "A · สายส่งไปส่ง" },
  { value: "B", label: "B · ลูกค้ามารับที่สาขา" },
];

export const serviceModeLabel = (mode: CustomerServiceMode | null | undefined) =>
  SERVICE_MODE_OPTIONS.find((option) => option.value === (mode ?? ""))?.label ??
  "ไม่ระบุ";

export const UNDELIVERED_REASON_LABEL: Record<SpecialOrderUndeliveredReason, string> = {
  SHOP_CLOSED: "ร้านปิด",
  CUSTOMER_REFUSED: "ลูกค้าไม่รับ",
  NO_STOCK: "ของไม่พอ",
  OTHER: "อื่นๆ",
};

export const undeliveredReasonLabel = (reason: string) =>
  UNDELIVERED_REASON_LABEL[reason as SpecialOrderUndeliveredReason] ?? reason;

/**
 * The van's "not delivered" report, but only while it still matters: once the order is
 * delivered or cancelled the report is history, not something to act on.
 */
export const activeUndelivered = (order: {
  status: SpecialOrderStatus;
  undelivered?: SpecialOrderUndelivered | null;
}): SpecialOrderUndelivered | null =>
  order.status === "ordered" && order.undelivered ? order.undelivered : null;

export const BILLTYPE_LABEL: Record<string, string> = {
  cash: "ขายสด",
  credit: "ขายเชื่อ",
};

export const billtypeLabel = (billtype: string | null | undefined) =>
  (billtype && BILLTYPE_LABEL[billtype]) || billtype || "-";

/** `IN` prices include VAT; anything else (EX, NA, unset) has VAT added on top. */
export const taxtypeLabel = (taxtype: string | null | undefined) =>
  taxtype === "IN" ? "ราคารวม VAT" : "ราคาไม่รวม VAT";

export const DOC_TYPE_LABEL: Record<SpecialOrderDocType, string> = {
  INVOICE: "ใบส่งสินค้า/ใบแจ้งหนี้ (IC)",
  CASHSALE: "ขายเงินสด (IV)",
};

/** Cash customer's handover channels. No QR in v1: nothing can generate one before the bill. */
export const PAYMENT_OPTIONS: { value: SpecialOrderCashPaymentType; label: string }[] = [
  { value: "CASH", label: "เงินสด" },
  { value: "BANK_TRANSFER", label: "โอน" },
];

export const PAYMENT_LABEL: Record<string, string> = {
  CASH: "เงินสด",
  QR_CODE: "QR",
  BANK_TRANSFER: "โอน",
  CREDIT: "ขายเชื่อ",
};

/** The existing bill detail screens, keyed by the document the order was billed as. */
export const billDetailPath = (
  billingUuid: string,
  docType: SpecialOrderDocType | null,
) =>
  docType === "CASHSALE"
    ? `/sales/cash-sales/details/${billingUuid}`
    : `/sales/invoice/details/${billingUuid}`;

export const API_DATE_FORMAT = "YYYY-MM-DD";

/** A contract date (`YYYY-MM-DD`) for display. Parsed as a plain date — no timezone shift. */
export const formatOrderDate = (date: string | null | undefined) =>
  date ? dayjs(date).format("DD/MM/YYYY") : "-";

export const todayApiDate = () => dayjs().format(API_DATE_FORMAT);

/** The message a failed request carries, localized where a translation exists. */
export const apiErrorMessage = (error: unknown): string => {
  const data = (error as { response?: { data?: unknown } } | null)?.response?.data as
    | { message?: unknown; error?: unknown; detail?: unknown }
    | undefined;
  if (!data || typeof data !== "object") return "";
  for (const candidate of [data.message, data.error, data.detail]) {
    if (typeof candidate === "string" && candidate) return candidate;
  }
  return "";
};

export const httpStatusOf = (error: unknown): number | undefined =>
  (error as { response?: { status?: number } } | null)?.response?.status;

/**
 * The orders a 409 SPECIAL_ORDER_PENDING_PICKUP says are still waiting. The contract names
 * the payload `{ orders }` without pinning which envelope field carries it, so both are read.
 */
export const pendingPickupOrders = (error: unknown): SpecialOrderPendingPickup[] => {
  const body = (error as { response?: { data?: unknown } } | null)?.response?.data as
    | { data?: unknown; error?: unknown }
    | undefined;
  if (!body || typeof body !== "object") return [];
  for (const slot of [body.error, body.data, body]) {
    const orders = (slot as { orders?: unknown } | null | undefined)?.orders;
    if (Array.isArray(orders)) return orders as SpecialOrderPendingPickup[];
  }
  return [];
};
